import { useQueries } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { core } from "..";
import { Formula, FormulaContext, RefSchemaItem, RowWithEmbeddedMetadata } from "@data-root/types";
import { useAppContext } from "@data-root/react";

export const useInlineFilter: any = (item: RefSchemaItem, rows: RowWithEmbeddedMetadata[], inlineFilter?: Formula) => {
  const { appSpecifier } = useAppContext();

  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  const [filteredRows, setFilteredRows] = useState<RowWithEmbeddedMetadata[]>([]);

  const partialContext = useMemo<FormulaContext>(
    () => ({
      appId: appSpecifier.appId,
      deploymentId: appSpecifier.deploymentId,
      tableId: item.ref,
      rowId: undefined,
      row: undefined,
    }),
    [appSpecifier.appId, appSpecifier.deploymentId, item.ref]
  );

  const { values, ready } = useQueries({
    queries: rows.map((row) => ({
      queryKey: ["inlineFilter", inlineFilter, partialContext, row],
      queryFn: async () => {
        if (!inlineFilter) return null;

        const result = await core.evaluateFormula<string>(inlineFilter, {
          ...partialContext,
          rowId: row.id,
          row,
        });

        return result;
      },
    })),
    combine: (results) => ({
      values: results.map((result) => result.data!),
      ready: results.every((result) => result.status === "success"),
    }),
  });

  const multiple = useMemo<boolean>(() => {
    if (!ready) return false;
    if (values.length === 0) return false;

    return Array.isArray(values[0]);
  }, [ready, values]);

  const uniqueValues = useMemo<string[]>(() => {
    if (!ready) return [];
    if (values.length === 0) return [];

    return [...new Set(values.flat())];
  }, [ready, values]);

  const filter = useCallback(
    (value: string) => {
      if (!ready) return;
      if (values.length === 0) return;

      if (selectedFilter === value) return setSelectedFilter(null);

      setSelectedFilter(value);
      setFilteredRows(
        rows.filter((_, i) => (Array.isArray(values[i]) ? values[i].includes(value) : values[i] === value))
      );
    },
    [ready, rows, selectedFilter, values]
  );

  return {
    values: uniqueValues,
    // filteredRows,
    filteredRows: selectedFilter ? filteredRows : rows,
    multiple,
    ready,
    filter,
    selectedFilter,
  };
};
